import {
  API_PREFIX,
  HttpError,
  paginatedFromDto,
  type HttpClient,
} from "@nasam/kernel";
import type { AssetTypesRepository } from "../domain";
import {
  archivedAssetTypeListResponseDtoSchema,
  archivedAssetTypeMatchResponseDtoSchema,
  assetTypeCreateDtoSchema,
  assetTypeDeleteResponseDtoSchema,
  assetTypeRecordResponseDtoSchema,
  assetTypeSummaryResponseDtoSchema,
  assetTypeUpdateDtoSchema,
  assetTypesListResponseDtoSchema,
} from "./assetTypeDtos";
import {
  archivedAssetTypeFromDto,
  archivedAssetTypeMatchFromDto,
  assetTypeCreateToDto,
  assetTypeFromDto,
  assetTypeSummaryFromDto,
  assetTypeUpdateToDto,
} from "./assetTypeMappers";

const ASSET_TYPES_PATH = `${API_PREFIX}/asset-types`;

export function createAssetTypesHttpRepository(
  http: HttpClient,
): AssetTypesRepository {
  return {
    async list(query) {
      const response = await http.get(ASSET_TYPES_PATH, { params: query });
      return paginatedFromDto(
        assetTypesListResponseDtoSchema.parse(response),
        assetTypeFromDto,
      );
    },
    async getSummary() {
      const response = await http.get(`${ASSET_TYPES_PATH}/summary`);
      return assetTypeSummaryFromDto(
        assetTypeSummaryResponseDtoSchema.parse(response).data,
      );
    },
    async getById(id) {
      const response = await http.get(`${ASSET_TYPES_PATH}/${id}`);
      return assetTypeFromDto(
        assetTypeRecordResponseDtoSchema.parse(response).data,
      );
    },
    async create(input) {
      try {
        const response = await http.post(
          ASSET_TYPES_PATH,
          assetTypeCreateDtoSchema.parse(assetTypeCreateToDto(input)),
        );
        const created = assetTypeRecordResponseDtoSchema.parse(response);
        return {
          status: "created",
          assetType: assetTypeFromDto(created.data),
          ...(created.message !== undefined
            ? { responseMessage: created.message }
            : {}),
        };
      } catch (error) {
        // Used to answer 201 with a trimmed match instead of a full record
        // when the name matched an archived type; now 409s instead, same
        // trimmed shape under `data` -- still not an error from the form's
        // own perspective, just an offer to restore. Disambiguated by
        // shape, not the code string, same as every other archived-match
        // endpoint in this app.
        const archived =
          error instanceof HttpError && error.status === 409
            ? archivedAssetTypeMatchResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (archived === undefined || !archived.success) throw error;
        return {
          status: "archived-match",
          match: archivedAssetTypeMatchFromDto(archived.data.data),
          message: archived.data.message,
        };
      }
    },
    async update(id, input) {
      const response = await http.put(
        `${ASSET_TYPES_PATH}/${id}`,
        assetTypeUpdateDtoSchema.parse(assetTypeUpdateToDto(input)),
      );
      const parsed = assetTypeRecordResponseDtoSchema.parse(response);
      return {
        ...assetTypeFromDto(parsed.data),
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },
    async delete(id) {
      const response = await http.delete(`${ASSET_TYPES_PATH}/${id}`);
      const { message } = assetTypeDeleteResponseDtoSchema.parse(response);
      return { responseMessage: message };
    },
    async restore(id) {
      const response = await http.patch(`${ASSET_TYPES_PATH}/${id}/restore`);
      const parsed = assetTypeRecordResponseDtoSchema.parse(response);
      const assetType = assetTypeFromDto(parsed.data);
      return {
        ...assetType,
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },
    async listArchived(query) {
      const response = await http.get(`${ASSET_TYPES_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedAssetTypeListResponseDtoSchema.parse(response),
        archivedAssetTypeFromDto,
      );
    },
  };
}
